import { useState, useEffect } from "react";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const genId = () => Math.random().toString(36).substr(2, 12);

const emptyItem = () => ({
  id: genId(),
  garment_type: "",
  collection_id: "",
  collection_name: "",
  fabric_id: "",
  fabric_name: "",
  quantity: 1,
  price_per_piece: 0,
  item_total: 0,
  custom_requests: "",
});

export default function ItemsStep({ items, setItems, fabrics, collections }) {
  const [garmentTypes, setGarmentTypes] = useState([]);

  useEffect(() => {
    axios.get(`${API}/garment-types`).then((r) => setGarmentTypes(r.data)).catch(console.error);
  }, []);

  const updateItem = (idx, updates) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...updates };
      next[idx].item_total = next[idx].quantity * next[idx].price_per_piece;
      return next;
    });
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const getCollectionsForType = (type) =>
    type ? collections.filter((c) => c.garment_type === type) : collections;

  const getFabricsForCollection = (colId) => {
    const col = collections.find((c) => c.id === colId);
    if (col?.fabric_ids?.length > 0) return fabrics.filter((f) => col.fabric_ids.includes(f.id));
    return fabrics;
  };

  return (
    <div className="animate-fade-in space-y-6" data-testid="items-step">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-[#9F7D23]" />
            <h3 className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24]">Order Items</h3>
          </div>
          <p className="text-sm text-[#8B7536]">Add products with type, collection, and fabric</p>
        </div>
        {items.length > 0 && (
          <button onClick={addItem} data-testid="add-item-btn" className="flex items-center gap-2 px-4 py-2 border border-[#9F7D23] text-[#9F7D23] font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#9F7D23] hover:text-white transition-all">
            <Plus className="w-4 h-4" /> Add Item
          </button>
        )}
      </div>

      {items.length === 0 && (
        <div className="border border-dashed border-[#D6D6CF] p-12 text-center">
          <p className="text-[#8B7536] text-sm mb-4">No items added yet</p>
          <button onClick={addItem} data-testid="add-first-item-btn" className="px-6 py-3 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all active:scale-95">
            Add First Item
          </button>
        </div>
      )}

      {items.map((item, idx) => {
        const availableCollections = getCollectionsForType(item.garment_type);
        const availableFabrics = getFabricsForCollection(item.collection_id);

        return (
          <div key={item.id} className="border border-[#D6D6CF] p-5 space-y-4 hover:border-[#9F7D23] transition-colors" data-testid={`item-${idx}`}>
            <div className="flex items-center justify-between">
              <span className="font-['Space_Mono'] text-xs uppercase tracking-widest text-[#9F7D23]">Item {idx + 1}</span>
              <button onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`} className="text-[#8B7536] hover:text-[#9F3A3A] transition-colors p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Type *</label>
                <Select value={item.garment_type} onValueChange={(v) => updateItem(idx, { garment_type: v, collection_id: "", collection_name: "", fabric_id: "", fabric_name: "" })}>
                  <SelectTrigger className="rounded-none font-['Space_Mono'] text-sm" data-testid={`item-type-${idx}`}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {garmentTypes.map((gt) => (
                      <SelectItem key={gt.id} value={gt.name}>{gt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Collection</label>
                <Select value={item.collection_id} onValueChange={(v) => { const col = collections.find((c) => c.id === v); updateItem(idx, { collection_id: v, collection_name: col?.name || "", fabric_id: "", fabric_name: "" }); }}>
                  <SelectTrigger className="rounded-none font-['Space_Mono'] text-sm" data-testid={`item-collection-${idx}`}>
                    <SelectValue placeholder="Select collection" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCollections.map((col) => (
                      <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Fabric *</label>
                <Select value={item.fabric_id} onValueChange={(v) => { const fab = fabrics.find((f) => f.id === v); updateItem(idx, { fabric_id: v, fabric_name: fab?.name || "" }); }}>
                  <SelectTrigger className="rounded-none font-['Space_Mono'] text-sm" data-testid={`item-fabric-${idx}`}>
                    <SelectValue placeholder="Select fabric" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFabrics.map((fab) => (
                      <SelectItem key={fab.id} value={fab.id}>{fab.name} ({fab.color})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Quantity</label>
                <input type="number" value={item.quantity} onChange={(e) => updateItem(idx, { quantity: Math.max(1, parseInt(e.target.value) || 1) })} min="1" data-testid={`item-qty-${idx}`} className="tinkah-input text-lg" />
              </div>
              <div>
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Price / Piece (AED)</label>
                <input type="number" value={item.price_per_piece || ""} onChange={(e) => updateItem(idx, { price_per_piece: parseFloat(e.target.value) || 0 })} placeholder="0" min="0" data-testid={`item-price-${idx}`} className="tinkah-input text-lg" />
              </div>
              <div>
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Subtotal</label>
                <div className="tinkah-input text-lg font-bold text-[#5C4D24]" data-testid={`item-subtotal-${idx}`}>{(item.quantity * item.price_per_piece).toFixed(0)} AED</div>
              </div>
            </div>

            <div>
              <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Custom Requests</label>
              <textarea value={item.custom_requests} onChange={(e) => updateItem(idx, { custom_requests: e.target.value })} placeholder="Special embroidery, beadwork, alterations..." rows={2} data-testid={`item-custom-${idx}`} className="tinkah-input resize-none text-sm" style={{ borderBottom: "2px solid #D6D6CF" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
